import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';

import { Observation } from 'types';
import { getObservationsByReference } from '../api/getObservationsByReference';

type UseGetObservationsByReferenceProps = {
  referenceId: string;
  referenceType: string;
  page?: number;
  rowsPerPage?: number;
  refetchKey?: number;
};

export const useGetObservationsByReference = ({
  referenceId,
  referenceType,
  page = 1,
  rowsPerPage = 10,
  refetchKey = 0,
}: UseGetObservationsByReferenceProps) => {
  const [observations, setObservations] = useState<Observation[]>([]);
  const [observationsTotal, setObservationsTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const fetchNotes = useCallback(
    async (controller: AbortController) => {
      if (!referenceId) return;

      try {
        setIsLoading(true);

        const fetchedData = await store.dispatch(
          getObservationsByReference({
            referenceId,
            referenceType,
            params: {
              page,
              items: rowsPerPage,
            },
            signal: controller.signal,
          })
        );

        setObservations(fetchedData?.observations || []);
        setObservationsTotal(fetchedData?.total || 0);

        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsError(true);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, rowsPerPage, referenceId, refetchKey]
  );

  useEffect(() => {
    const controller = new AbortController();

    fetchNotes(controller);

    return () => controller.abort();
  }, [fetchNotes]);

  return {
    data: { observations, observationsTotal },
    isLoading,
    isError,
  };
};
